import { BsPersonAdd } from "react-icons/bs";
import { getAllCompanyStaffById, activateDeactivateTeam, getAllTeams, getASingleTeam, getCompaniesByType, getSlackWorkspaces, sendStaffInviteLink, getAllCompaniesByType, getCompanyOnboardingStatus, getCompanyPortfolio, getAllPortfolios, getActiveRiskTypes } from "../api/requests";
import EditTeam from "../views/partners/EditTeamModal";
import InviteTeamMember from "../views/partners/InviteTeamMemberModal";
import useModal from "./modalHook";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import AddTeam from "../views/partners/addTeamModal";
import AddPartner from "../views/partners/AddPartnerModal";
import NewMessage from "../views/partners/NewMessage";
import AssignMember from "../views/partners/assignMemberModal";
import AssignTeamToPartner from "../views/partners/AddTeamToPartnerModal";
import AssignRelationshipManager from "../views/partners/AssignRelationshipMagerModal";
import SetCommission from "../views/partners/SetCommission";
import SetStaffSupervisor from "../views/partners/SetStaffSupervisor";
import UpdatePartnerQuota from "../views/partners/fuel/UpdatePartnerQuota";
import UploadLogo from "../components/elements/UploadLogo";
import AddTeamToWorkspace from "../views/partners/AddTeamToWorkspaceModal";
import { MdGroups } from "react-icons/md";
import { Company } from "../models/company/Company";
import { ICompany } from "../models/company/ICompany";
import { User } from "../models/User";
import { COMPANY_TYPES } from "../resources/enums";
import GenerateAgencyPayoutModal from "../views/partners/GenerateAgencyPayoutModal";
import SetTeamLead from "../views/partners/setTeamLead";
import { Staff } from "../models/staff/Staff";
import { IStaff } from "../models/staff/IStaff";
import AgentLinkModal from "../views/partners/agency/team/AgentLinkModal";
import { ITablePaginationData } from "../components/elements/GTable/GTable";
import { useState } from "react";
import ActionConfirmation from "../components/elements/ActionConfirmation";
import ChangeAccessRight from "../views/partners/ChangeAccessRightModal";
import SetStaffToBranch from "../views/shared/settings/branch/SetStaffToBranch";
import SetPermissionsModal from "../views/partners/SetPremissionModal";
import { getAllInsurers } from "../services/insurer";
import { useSearchParams } from "react-router-dom";
import { showToast } from "../utils/miscFunctions";

const usePartner = () => {
  const { ModalComponent, closeModal, updateModalStates } = useModal();
  const PartnerModal = ModalComponent;
  const queryClient = useQueryClient();
  const [companiesPaginationData, setCompaniesPaginationData] = useState<ITablePaginationData>({
    total: 0,
    per_page: 0,
    totalPages: 0,
    initialPage: 0,
    from: 0,
    to: 0,
  });

  const fetchAllCompanies = (countryId: number, page: number, size: number, companyTypes?: COMPANY_TYPES[]): Promise<Company[]> => {
    return new Promise<Company[]>((resolve, reject) => {
      getAllTeams(countryId, page, size, companyTypes)
        .then((response: any) => {
          const companies: Company[] = [];
          if (response) {
            response?.content?.forEach((company: ICompany) => {
              companies.push(new Company(company));
            });

            const from = response.pageable?.offset + 1;
            const to =
              from + response.size > response.totalElements
                ? response.totalElements
                : from + response.size - 1;

            setCompaniesPaginationData(companiesPaginationData => ({
              ...companiesPaginationData,
              per_page: response.size,
              total: response.totalElements,
              totalPages: response.totalPages,
              from: from ?? 0,
              to: to ?? 0,
              initialPage: page
            }));
            resolve(companies);
          }
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const fetchAllCompaniesByType = (companyType: COMPANY_TYPES | undefined): Promise<Company[]> => {
    return new Promise<Company[]>((resolve, reject) => {
      getCompaniesByType(companyType)
        .then((response: any) => {
          const companies: Company[] = [];
          if (response) {
            response?.data?.forEach((company: ICompany) => {
              companies.push(new Company(company));
            });
          }
          resolve(companies);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const fetchAllCompaniesByTypes = (companyTypes: COMPANY_TYPES[]): Promise<Company[]> => {
    return new Promise<Company[]>((resolve, reject) => {
      getAllCompaniesByType(companyTypes)
        .then((response: any) => {
          const companies: Company[] = [];
          if (response) {
            response?.data?.forEach((company: ICompany) => {
              companies.push(new Company(company));
            });
          }
          resolve(companies);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const getSingleCompany = (id: string) => {
    return new Promise<Company>((resolve, reject) => {
      getASingleTeam(id)
        .then((response: any) => {
          if (response) {
            resolve(new Company(response?.data));
          }
        })
        .catch((error: Error) => {
          reject(error);
        })
    });
  };

  const getAllCompanyStaff = async (teamID?: number, isBroker?: boolean) => {
    if (!teamID) return;
    try {
      const response = await getAllCompanyStaffById(teamID)

      if (response) {
        return response?.map((staffMember: IStaff) => new Staff(staffMember));
      }
      if (response && isBroker) {
        return response?.data?.map((staffMember: IStaff) => new Staff(staffMember));
      }
    } catch (error) { }
  };

  const fetchAllInsurers = (countryId?: number) => {
    if (!countryId) return;
    return new Promise<any>((resolve, reject) => {
      getAllInsurers(countryId)
        .then((response: any) => {
          resolve(response?.data);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const getAllSlackWorkspaces = () => {
    return new Promise<any>((resolve, reject) => {
      getSlackWorkspaces()
        .then((response: any) => {
          resolve(response?.data);
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  };

  const inviteMember = (team: any, queryKey: string) => {
    updateModalStates({
      show: true,
      size: '2xl',
      showCloseButton: true,
      children: (
        <InviteTeamMember
          onProceed={() => {
            queryClient.invalidateQueries({ queryKey: [queryKey] });
            closeModal();
          }}
          onClose={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: [queryKey] });
          }}
          team={team}
        />
      ),
      showTitleSection: true,
      classes: '!p-0 r-[5px]',
      title: 'Invite team member',
      yellowHeader: true
    });
  };

  const editTeam = (team: any) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <EditTeam
          team={team}
          onProceed={(response: any) => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['getAllCompanies'] });
          }}
          onClose={closeModal}
        />
      ),
      showTitleSection: true,
      classes: '!p-0 r-[5px]',
      title: 'Edit company info',
      yellowHeader: true,
      modalIcon: <BsPersonAdd className="h-5 w-5" />
    });
  };

  const addNewCompany = () => {
    updateModalStates({
      showCloseButton: false,
      show: true,
      size: 'md',
      children: (
        <AddTeam
          onProceed={(res: any) => {
            queryClient.invalidateQueries({ queryKey: ['fetchAllCompanies'] });
            closeModal();
          }}
          onClose={closeModal}
        />
      ),

      classes: '!p-0 r-[5px]',
      title: 'Add New Company'
    });
  };

  const addNewPartner = () => {
    updateModalStates({
      showCloseButton: false,
      show: true,
      size: 'md',
      children: (
        <AddPartner
          onProceed={(res: any) => {
            queryClient.invalidateQueries({ queryKey: ['getPartners'] });
            closeModal();
          }}
          onClose={closeModal}
        />
      ),

      classes: '!p-0 r-[5px]'
    });
  };

  const addNewMessage = () => {
    updateModalStates({
      show: true,
      size: '2xl',
      children: (
        <NewMessage
          onProceed={() => {
            closeModal();
          }}
          onClose={closeModal}
        />
      ),

      classes: '!p-0 r-[5px]'
    });
  };

  const assignMemberToTeam = (company: Company, queryKey: string, fromUser?: boolean, user?: User,) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <AssignMember
          teamID={company.id}
          fromUser={fromUser}
          user={user}
          onProceed={() => {
            queryClient.invalidateQueries({ queryKey: [queryKey] });
            closeModal();
          }}
          onClose={closeModal}
        />
      ),
      title: 'Assign member to company',
      classes: '!p-0 r-[5px]'
    });
  };

  const assignTeamToPartner = (team: any) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <AssignTeamToPartner
          teamID={team.id}
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['getAllCompanies'] });
          }}
          onClose={closeModal}
        />
      ),
      title: 'Assign team to partner',
      classes: '!p-0 r-[5px]'
    });
  };

  const assignRelationshipManager = (team: any) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <AssignRelationshipManager
          teamID={team.id}
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['getAllCompanies'] });
          }}
          onClose={closeModal}
        />
      ),
      title: 'Set relationship manager',
      classes: '!p-0 r-[5px]'
    });
  };

  const setCommissionPercentage = (team: any) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <SetCommission
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['getAllCompanies'] });
          }}
          onClose={closeModal}
          teamID={team?.id}
        />
      ),
      title: 'Set commission rate',
      classes: '!p-0 r-[5px]'
    });
  };

  const setStaffSupervisor = (staffId: number) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <SetStaffSupervisor
          staffId={staffId}
          onProceed={() => {
            closeModal();
          }}
          onClose={closeModal}
        />
      ),
      showTitleSection: true,
      classes: '!p-0 r-[5px]',
      yellowHeader: true,
      title: 'Assign supervisor to staff'
    });
  };

  const assignStaffToBranchModal = (staffId: number) => {
    updateModalStates({
      show: true,
      children: (
        <SetStaffToBranch
          onClose={closeModal}
          onProceed={() => {
            queryClient.invalidateQueries({ queryKey: ['getAllCompanyStaff'] });
            closeModal();
          }}
          staffId={staffId}
          isDeletion={false}
          title="ADD"
        />
      ),
      size: 'md',
      showTitleSection: true,
      title: 'Assign staff to branch'
    });

  }

  const deleteStaffFromBranchModal = (staffId: number, branch: string) => {
    updateModalStates({
      show: true,
      children: (
        <SetStaffToBranch
          onClose={closeModal}
          onProceed={() => {
            queryClient.invalidateQueries({ queryKey: ['getAllCompanyStaff'] });
            closeModal();
          }}
          staffId={staffId}
          branch={branch}
          isDeletion={true}
          title="DELETE"
        />
      ),
      size: 'md',
      showTitleSection: true,
      title: 'Edit branch'
    });

  }

  const uploadPartnerLogo = (partnerId: string) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <UploadLogo
          partnerId={partnerId}
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['getPartners'] });
          }}
          onClose={closeModal}
        />
      ),

      classes: '!p-0 r-[5px]'
    });
  };

  const updatePartnerFuelQuota = (partnerId: string) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <UpdatePartnerQuota
          partnerId={partnerId}
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['getPartners'] });
          }}
          onClose={closeModal}
        />
      ),

      classes: '!p-0 r-[5px]'
    });
  };

  const addTeamToSlackWorkspace = (team: any) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <AddTeamToWorkspace
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['getAllCompanies'] });
          }}
          team={team}
          onClose={closeModal}
        />
      ),
      showTitleSection: true,
      classes: '!p-0 r-[5px]',
      title: 'Add to workspace',
      yellowHeader: true,
      modalIcon: <MdGroups className="h-5 w-5" />
    });
  };

  const assignTeamLead = (id: string) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <SetTeamLead
          teamID={id}
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['getSingleCompany'] });
          }}
          onClose={closeModal}
        />
      ),
      title: 'Set team lead',
      classes: '!p-0 r-[5px]'
    });
  };

  const generateAnAgencyPayout = async (id: string) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <GenerateAgencyPayoutModal
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['getSingleCompany'] });
          }}
          onClose={closeModal}
          teamID={id ?? ''}
        />
      ),
      title: 'Generate payout',
      classes: '!p-0 r-[5px]'
    });
  };

  const assignMember = (id: string) => {
    updateModalStates({
      show: true,
      size: 'md',
      children: (
        <AssignMember
          teamID={id ?? ''}
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['getSingleCompany'] });
          }}
          onClose={closeModal}
        />
      ),
      title: 'Move member to company',
      classes: '!p-0 r-[5px]'
    });
  };

  const updateCompanyStatus = (status: boolean, id: number) => {
    console.log("status",status)
    updateModalStates({
      show: true,
      size: 'sm',
      children: (
        <ActionConfirmation
          type={status ? "warning" : "success"}
          onClose={closeModal}
          action={status ? "deactivate" : "activate"}
          onConfirm={() => {
            activateDeactivateTeam(!status, id)
              .then((response) => {
                showToast.success('Company status updated successfully');
                queryClient.invalidateQueries({ queryKey: ['fetchAllCompanies'] })
              })
              .catch((error: Error) => { console.log("error", error) })
              .finally(() => closeModal())
          }}
        />
      ),
      showTitleSection: true,
      classes: '!p-0 r-[5px]',
      yellowHeader: true,
      title: status ? 'Deactivate Company' : 'Activate Company'
    });
  };

  const showAgentLinkModal = (agentLink: string) => {
    updateModalStates({
      show: true,
      size: '2xl',
      children: (
        <AgentLinkModal
          onProceed={() => {
            closeModal();
            queryClient?.invalidateQueries({ queryKey: ['getTeamMembers'] });
          }}
          onClose={closeModal}
          link={agentLink}
        />
      ),
      title: 'Generated Agent Link',
      classes: '!p-0 r-[5px]'
    });
  };

  const resendInviteEmail = async (data: any) => {
    try {
      if (!data) return;
      const response = await sendStaffInviteLink(data)
      if (response) {
        showToast.success(response.message);
      }
    } catch (error: any) {
      showToast.error(error.description);
    }
  };

  const changeMemberAccessRight = async (data: any) => {
    updateModalStates({
      show: true,
      size: 'sm',
      children: (
        <ChangeAccessRight
          staffID={data.id}
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['getAllCompanyStaff'] })
          }}
          onClose={closeModal}
        />
      ),
      title: 'Change Access Right',
      classes: '!p-0 r-[5px]'
    });
  };

  const setUserPermission = async (data: any) => {
    updateModalStates({
      show: true,
      size: '6xl',
      children: (
        <SetPermissionsModal
          staffID={data.id}
          staffCompanyType={data.companyType}
          onProceed={() => {
            closeModal();
            queryClient.invalidateQueries({ queryKey: ['getAllCompanyStaff'] })
          }}
          onClose={closeModal}
        />
      ),
      title: 'Set User Permissions',
      classes: '!p-0 r-[5px]'
    });
  };

  const fetchCompanyOnboardingStatus = async () => {
    try {
      const response: any = await getCompanyOnboardingStatus();
      return response;
    } catch (error: any) {
      showToast.error(error.description);
    }
  };

  const fetchCompanyPortfolio = async (companyId: number) => {
    try {
      const response: any = await getCompanyPortfolio(companyId);
      return response;
    } catch (error: any) {
      showToast.error(error.description);
    }
  }

  const fetchAllPortfolios = async () => {
    try {
      const response: any = await getAllPortfolios();
      return response;
    } catch (error: any) {
      showToast.error(error.description);
    }
  }

  const fetchAllActiveRiskTypes = async () => {
    try {
      const response: any = await getActiveRiskTypes();
      if (response) {
        console.log("risk types", response);
        return response;
      }
    } catch (error: any) {
      console.error('Error fetching risk types:', error);
    }
  };



  return {
    fetchAllCompanies,
    getAllSlackWorkspaces,
    fetchAllCompaniesByType,
    getAllCompanyStaff,
    inviteMember,
    editTeam,
    addNewCompany,
    addNewPartner,
    addNewMessage,
    assignMemberToTeam,
    assignTeamToPartner,
    assignRelationshipManager,
    setStaffSupervisor,
    uploadPartnerLogo,
    updatePartnerFuelQuota,
    addTeamToSlackWorkspace,
    PartnerModal,
    getSingleCompany,
    assignTeamLead,
    generateAnAgencyPayout,
    setCommissionPercentage,
    assignMember,
    showAgentLinkModal,
    fetchAllInsurers,
    resendInviteEmail,
    companiesPaginationData,
    updateCompanyStatus,
    changeMemberAccessRight,
    fetchAllCompaniesByTypes,
    fetchCompanyOnboardingStatus,
    assignStaffToBranchModal,
    deleteStaffFromBranchModal,
    fetchCompanyPortfolio,
    setUserPermission,
    fetchAllPortfolios,
    fetchAllActiveRiskTypes,
  };
};

export default usePartner;
